/**
 * Mail's own side of combined search: `search()` from `@wtfalch/email/mailbox`
 * (RFC 8621 §4.4.1's free-text filter), turned into rows Mail opens by
 * `mailboxId`/`threadId`.
 *
 * Returns the matched `ThreadSummary` rows alongside the `SearchRow`s, not
 * just the rows: `people.ts` derives the People group from the same threads
 * rather than searching mail a second time.
 */
import type { MailClient, ThreadSummary } from '@wtfalch/email/mailbox';
import { search as searchThreads } from '@wtfalch/email/mailbox';

import { displayName } from '../mail/format.ts';
import type { AgoraRoute } from '../routes.ts';
import type { SearchRow } from './types.ts';

export type MailSearchOutcome = {
  rows: readonly SearchRow[];
  threads: readonly ThreadSummary[];
};

const DEFAULT_LIMIT = 20;

function routeFor(agoraId: string, thread: ThreadSummary): AgoraRoute {
  return {
    kind: 'agora',
    agoraId,
    section: 'mail',
    // A thread can sit in more than one mailbox (`mailboxIds`); the first is
    // the one Mail's list column opens on -- there is no "no mailbox, just
    // this thread" route (`routes.ts`'s own `MailLocation`), so a thread with
    // none (RFC 8621 does not allow it, but nothing here trusts that) falls
    // back to `null`, the same as landing with nothing chosen yet.
    location: { mailboxId: thread.mailboxIds[0] ?? null, threadId: thread.id },
  };
}

export async function searchMail(
  client: MailClient,
  agoraId: string,
  query: string,
  options?: { limit?: number },
): Promise<MailSearchOutcome> {
  // Errors are left to throw as whatever `search()` throws (always a
  // `MailError` -- see `@wtfalch/email/mailbox`'s own `errors.ts`); the
  // caller (`model.ts`'s `runSearch`) is the one place that turns a thrown
  // error into a group's one-line message, the same way `Mail.tsx`'s own
  // `report` is the one place that does it for the section itself.
  const page = await searchThreads(client, query, { limit: options?.limit ?? DEFAULT_LIMIT });
  const threads = page.items;
  const rows: SearchRow[] = threads.map((thread) => ({
    id: thread.id,
    title: thread.subject || '(no subject)',
    snippet:
      [thread.from && displayName(thread.from), thread.preview].filter(Boolean).join(' · ') ||
      undefined,
    time: thread.receivedAt,
    route: routeFor(agoraId, thread),
  }));
  return { rows, threads };
}
